import React, { useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { BarChartOutlined } from '@mui/icons-material';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ChartTemplate from '../../shared_components/templates/barChartTemplate';
import { formatGradeList, formatGrade } from '../../../utils/schoolUtils';

const GeoXEnrollmentCompositionChanges = ({
  selectedSchool,
  schoolsEnrollmentData,
  filteredSchools,
  selectedGrades,
  governanceFilter,
  timeframe,
  enrollmentTrends,
  animationEnabled = true
}) => {
  const theme = useTheme();
  const [viewType, setViewType] = useState('current');

  const handleViewTypeChange = (event, newValue) => {
    if (newValue !== null) {
      setViewType(newValue);
    }
  };

  // Check if selected school has demographic data
  const selectedSchoolHasDemographicData = useMemo(() => {
    if (!selectedSchool || !schoolsEnrollmentData?.length || enrollmentTrends?.isDropAPinLocation) {
      return true; // For drop-a-pin locations, we always show nearby schools only
    }
    
    const selectedSchoolData = schoolsEnrollmentData.find(d => d.uuid === selectedSchool.uuid);
    if (!selectedSchoolData?.race_data?.current) {
      return false;
    }
    
    // Check if there's any actual demographic data across selected grades
    const hasAnyData = selectedGrades.some(grade => {
      const formattedGrade = formatGrade(grade);
      const gradeData = selectedSchoolData.race_data.current[formattedGrade];
      return gradeData && Object.values(gradeData).some(count => count > 0);
    });
    
    return hasAnyData;
  }, [selectedSchool, schoolsEnrollmentData, selectedGrades, enrollmentTrends?.isDropAPinLocation]);

  const processedData = useMemo(() => {
    if (!selectedSchool || !schoolsEnrollmentData?.length) {
      return [];
    }

    // Handle drop-a-pin locations or schools without demographic data
    if (enrollmentTrends?.isDropAPinLocation || !selectedSchoolHasDemographicData) {
      // For drop-a-pin locations or schools without data, use only nearby schools data
      const nearbySchoolsData = filteredSchools
        .map(school => schoolsEnrollmentData.find(d => d.uuid === school.uuid))
        .filter(Boolean);

      if (!nearbySchoolsData.length) {
        return [];
      }

      // Process demographic data for nearby schools only
      return processNearbySchoolsDemographics(nearbySchoolsData);
    }
  
    // Function to process demographics for nearby schools only (for drop-a-pin locations)
    function processNearbySchoolsDemographics(nearbySchoolsData) {

      // Calculate totals by summing across selected grades
      const calculateTotals = (raceData) => {
        if (!raceData) {
          return {};
        }
        const totals = {};
        
        selectedGrades.forEach(grade => {
          const formattedGrade = formatGrade(grade);
          const gradeData = raceData[formattedGrade] || {};
          
          Object.entries(gradeData).forEach(([race, count]) => {
            totals[race] = (totals[race] || 0) + (count || 0);
          });
        });

        return totals;
      };

      // Get totals for all nearby schools
      const nearbyTotals = {
        current: {},
        comparison: {}
      };

      nearbySchoolsData.forEach(school => {
        const currentTotals = calculateTotals(school.race_data?.current);
        const comparisonTotals = calculateTotals(school.race_data?.comparison);

        Object.entries(currentTotals).forEach(([race, count]) => {
          nearbyTotals.current[race] = (nearbyTotals.current[race] || 0) + count;
        });

        Object.entries(comparisonTotals).forEach(([race, count]) => {
          nearbyTotals.comparison[race] = (nearbyTotals.comparison[race] || 0) + count;
        });
      });

      // Calculate overall totals
      const totalEnrollment = {
        current: Object.values(nearbyTotals.current).reduce((sum, val) => sum + (val || 0), 0),
        comparison: Object.values(nearbyTotals.comparison).reduce((sum, val) => sum + (val || 0), 0)
      };

      if (totalEnrollment.current === 0) {
        return [];
      }

      // Calculate percentages for current and comparison years
      const currentPercentages = {};
      const comparisonPercentages = {};

      Object.entries(nearbyTotals.current).forEach(([race, count]) => {
        currentPercentages[race] = (count / totalEnrollment.current) * 100;
      });

      Object.entries(nearbyTotals.comparison).forEach(([race, count]) => {
        comparisonPercentages[race] = totalEnrollment.comparison > 0 
          ? (count / totalEnrollment.comparison) * 100 
          : 0;
      });

      // Format data for the chart - use actual field names from data
      const raceCategories = [
        { key: 'white', label: 'White' },
        { key: 'hispanic', label: 'Hispanic/Latino' },
        { key: 'black', label: 'Black/African American' },
        { key: 'asian', label: 'Asian' },
        { key: 'american_indian', label: 'American Indian/Alaska Native' },
        { key: 'pacific_islander', label: 'Native Hawaiian/Other Pacific Islander' },
        { key: 'two_or_more_races', label: 'Two or More Races' }
      ];
      

      
      const result = raceCategories
        .filter(race => currentPercentages[race.key] >= 5) // Only show groups with 5% or more
        .map(race => ({
          category: race.label,
          current: Math.round(currentPercentages[race.key] || 0),
          comparison: Math.round(comparisonPercentages[race.key] || 0),
          currentCount: nearbyTotals.current[race.key] || 0,
          comparisonCount: nearbyTotals.comparison[race.key] || 0,
          trend: nearbyTotals.comparison[race.key] > 0 
            ? ((nearbyTotals.current[race.key] - nearbyTotals.comparison[race.key]) / nearbyTotals.comparison[race.key]) * 100 
            : 0
        }));

      return result;
    }
  
    // Get selected school data for regular schools
    const selectedSchoolData = schoolsEnrollmentData.find(
      d => d.uuid === selectedSchool.uuid
    );
  
    if (!selectedSchoolData) {
      return [];
    }
  
    // Filter and prepare other schools data
    const otherSchools = filteredSchools
      .filter(school => school.uuid !== selectedSchool.uuid)
      .map(school => schoolsEnrollmentData.find(d => d.uuid === school.uuid))
      .filter(Boolean);

    // Calculate totals by summing across selected grades
    const calculateTotals = (raceData) => {
      if (!raceData) {

        return {};
      }
      const totals = {};

      
      selectedGrades.forEach(grade => {
        const formattedGrade = formatGrade(grade);
        const gradeData = raceData[formattedGrade] || {};

        
        Object.entries(gradeData).forEach(([race, count]) => {
          totals[race] = (totals[race] || 0) + (count || 0);
        });
      });

      return totals;
    };

    // Get selected school totals
    const selectedTotals = {
      current: calculateTotals(selectedSchoolData.race_data?.current),
      comparison: calculateTotals(selectedSchoolData.race_data?.comparison)
    };

    // Get nearby schools totals
    const nearbyTotals = {
      current: {},
      comparison: {}
    };

    otherSchools.forEach(school => {
      const currentTotals = calculateTotals(school.race_data?.current);
      const comparisonTotals = calculateTotals(school.race_data?.comparison);

      Object.entries(currentTotals).forEach(([race, count]) => {
        nearbyTotals.current[race] = (nearbyTotals.current[race] || 0) + count;
      });

      Object.entries(comparisonTotals).forEach(([race, count]) => {
        nearbyTotals.comparison[race] = (nearbyTotals.comparison[race] || 0) + count;
      });
    });

    // Calculate overall totals
    const totalEnrollment = {
      current: Object.values(selectedTotals.current).reduce((sum, val) => sum + (val || 0), 0),
      comparison: Object.values(selectedTotals.comparison).reduce((sum, val) => sum + (val || 0), 0)
    };

    const nearbyTotalEnrollment = {
      current: Object.values(nearbyTotals.current).reduce((sum, val) => sum + (val || 0), 0),
      comparison: Object.values(nearbyTotals.comparison).reduce((sum, val) => sum + (val || 0), 0)
    };

    // Process race categories
    const raceCategories = [
      'hispanic',
      'white',
      'black',
      'asian',
      'pacific_islander',
      'american_indian',
      'two_or_more_races'
    ];

    const results = raceCategories.map(race => {
      const schoolCurrent = selectedTotals.current[race] || 0;
      const schoolComparison = selectedTotals.comparison[race] || 0;
      const nearbyCurrent = nearbyTotals.current[race] || 0;
      const nearbyComparison = nearbyTotals.comparison[race] || 0;

      const schoolValue = totalEnrollment.current > 0 
        ? (schoolCurrent / totalEnrollment.current) * 100 
        : 0;
      
      const nearbyValue = nearbyTotalEnrollment.current > 0
        ? (nearbyCurrent / nearbyTotalEnrollment.current) * 100
        : 0;

      const schoolChange = schoolComparison > 0
        ? ((schoolCurrent - schoolComparison) / schoolComparison) * 100
        : 0;

      const nearbyChange = nearbyComparison > 0
        ? ((nearbyCurrent - nearbyComparison) / nearbyComparison) * 100
        : 0;

      return {
        category: race.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
        value: schoolValue,
        nearbyValue: nearbyValue,
        schoolCount: schoolCurrent,
        nearbyCount: nearbyCurrent,
        schoolChange: schoolChange,
        nearbyChange: nearbyChange
      };
    });

    return results
      .filter(item => item.value >= 5 || item.nearbyValue >= 5)
      .sort((a, b) => b.value - a.value);
  }, [selectedSchool, schoolsEnrollmentData, filteredSchools, selectedGrades, enrollmentTrends?.isDropAPinLocation, selectedSchoolHasDemographicData]);


  const getAnalysisSummary = useMemo(() => {
    if (!processedData.length || !selectedSchool?.name) return '';
    
    // Handle drop-a-pin locations or schools without demographic data
    if (enrollmentTrends?.isDropAPinLocation || !selectedSchoolHasDemographicData) {
      if (viewType === 'current') {
        const mostPopulous = [...processedData]
          .sort((a, b) => b.current - a.current)[0];
        
        return `${mostPopulous.category} students comprise ${Math.round(mostPopulous.current)}% of nearby schools enrollment`;
      } else {
        const biggestChange = [...processedData]
          .sort((a, b) => Math.abs(b.trend) - Math.abs(a.trend))[0];
        
        if (Math.abs(biggestChange.trend) < 0.5) {
          return `${biggestChange.category} student enrollment has not changed (0%) over five years in nearby schools`;
        }
        
        const changeDirection = biggestChange.trend >= 0 ? 'increased' : 'decreased';
        return `${biggestChange.category} student enrollment has ${changeDirection} by ${Math.abs(Math.round(biggestChange.trend))}% over five years in nearby schools`;
      }
    }
    
    // Handle regular schools with demographic data
    if (viewType === 'current') {
      const mostPopulous = [...processedData]
        .sort((a, b) => b.value - a.value)[0];
      
      const diffText = mostPopulous.value > mostPopulous.nearbyValue
        ? `${Math.round(mostPopulous.value - mostPopulous.nearbyValue)}% higher than`
        : `${Math.round(mostPopulous.nearbyValue - mostPopulous.value)}% lower than`;
    
      return `${mostPopulous.category} students comprise ${Math.round(mostPopulous.value)}% of enrollment, ${diffText} nearby schools`;
    } else {
      const biggestChange = [...processedData]
        .sort((a, b) => Math.abs(b.schoolChange) - Math.abs(a.schoolChange))[0];
      
      const timeframeText = timeframe?.hasComparison 
        ? 'over five years'
        : `since ${timeframe?.startLabel || '2019'}`;
      
      if (Math.abs(biggestChange.schoolChange) < 0.5) {
        return `${biggestChange.category} student enrollment has not changed (0%) ${timeframeText}`;
      }
      
      const changeDirection = biggestChange.schoolChange >= 0 ? 'increased' : 'decreased';
      return `${biggestChange.category} student enrollment has ${changeDirection} by ${Math.abs(Math.round(biggestChange.schoolChange))}% ${timeframeText}`;
    }
  }, [processedData, selectedSchool?.name, viewType, timeframe, enrollmentTrends?.isDropAPinLocation, selectedSchoolHasDemographicData]);

  const tooltipText = useMemo(() => {
    if (viewType === 'current') {
      return "Shows current demographic composition. Note: Private school data is from 2021, public school data is from 2023.";
    }

    if (!timeframe) {
      return "Shows demographic enrollment changes. Note: Private and public schools may use different data years.";
    }

    const timeframeText = timeframe.hasComparison
      ? "Shows 5-year demographic enrollment changes (since 2019-20). Note: Data years may vary between private and public schools."
      : `Shows demographic enrollment changes since ${timeframe.startLabel} (school's first year). Note: Data years may vary between private and public schools.`;

    return timeframeText;
  }, [viewType, timeframe]);
  
  const additionalInfo = useMemo(() => {
    let baseText;
    
    if (enrollmentTrends?.isDropAPinLocation) {
      baseText = "Only showing demographic groups that comprise 5% or more of enrollment in nearby schools";
    } else if (!selectedSchoolHasDemographicData) {
      baseText = "Only showing demographic groups that comprise 5% or more of enrollment in nearby schools. This school does not have disaggregated demographic data available";
    } else {
      baseText = "Only showing demographic groups that comprise 5% or more of enrollment in either the selected school or nearby schools";
    }
    
    if (viewType === 'trend' && timeframe && selectedSchoolHasDemographicData && !enrollmentTrends?.isDropAPinLocation) {
      let note = `Note: ${selectedSchool.name}'s data starts from ${timeframe.startLabel}`;
      if (timeframe.hasExpandedGrades) {
        note += `, when it served grades ${formatGradeList(timeframe.startGrades)}. The school now serves grades ${formatGradeList(timeframe.endGrades)}`;
      }
      return `${baseText}. ${note}.`;
    }
    
    return baseText;
  }, [viewType, timeframe, selectedSchool?.name, enrollmentTrends?.isDropAPinLocation, selectedSchoolHasDemographicData]);

  const chartData = useMemo(() => {
    if (!processedData.length) return null;
  
    // Handle drop-a-pin locations or schools without demographic data (show only area data)
    if (enrollmentTrends?.isDropAPinLocation || !selectedSchoolHasDemographicData) {
      return {
        labels: processedData.map(d => d.category),
        datasets: [
          {
            data: viewType === 'current' 
              ? processedData.map(d => Math.round(d.current * 10) / 10)
              : processedData.map(d => Math.round(d.trend * 10) / 10),
            backgroundColor: 'rgba(180, 226, 180, 0.8)',
            borderColor: 'rgba(180, 226, 180, 1)',
            borderWidth: 1,
            borderRadius: 4,
            barPercentage: 0.4,
            categoryPercentage: 0.8,
          }
        ]
      };
    }
  
    // Handle regular schools (show selected school vs nearby schools)
    return {
      labels: processedData.map(d => d.category),
      datasets: [
        {
          data: viewType === 'current' 
            ? processedData.map(d => Math.round(d.value * 10) / 10)
            : processedData.map(d => Math.round(d.schoolChange * 10) / 10),
          backgroundColor: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.4,
          categoryPercentage: 0.8,
        },
        {
          data: viewType === 'current'
            ? processedData.map(d => Math.round(d.nearbyValue * 10) / 10)
            : processedData.map(d => Math.round(d.nearbyChange * 10) / 10),
          backgroundColor: 'rgba(180, 226, 180, 0.8)',
          borderColor: 'rgba(180, 226, 180, 1)',
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.4,
          categoryPercentage: 0.8,
        }
      ]
    };
  }, [processedData, viewType, theme.palette.primary.main, enrollmentTrends?.isDropAPinLocation, selectedSchoolHasDemographicData]);

  // Update the chartOptions object in GeoXEnrollmentCompositionChanges:
const chartOptions = useMemo(() => ({
  indexAxis: 'y',
  plugins: {
    legend: {
      display: !enrollmentTrends?.isDropAPinLocation && selectedSchoolHasDemographicData,
      position: 'bottom',
      labels: {
        generateLabels: () => {
          if (enrollmentTrends?.isDropAPinLocation || !selectedSchoolHasDemographicData) {
            return [{
              text: enrollmentTrends?.isDropAPinLocation ? 'Area Schools' : 'Nearby Schools',
              fillStyle: 'rgba(180, 226, 180, 0.8)',
              strokeStyle: 'rgba(180, 226, 180, 1)',
            }];
          }
          
          return [{
            text: selectedSchool.name,
            fillStyle: theme.palette.primary.main,
            strokeStyle: theme.palette.primary.main,
          }, {
            text: 'Nearby Schools',
            fillStyle: 'rgba(180, 226, 180, 0.8)',
            strokeStyle: 'rgba(180, 226, 180, 1)',
          }];
        }
      }
    },
    datalabels: {
      // A bar this short has no room for a label inside it, and a zero has nothing to
      // report. Magnitude decides rather than sign, so a decline keeps its label. Values
      // arrive with one decimal for the axis; the labels read better rounded.
      formatter: (value) => (!value || Math.abs(value) < 5 ? '' : `${Math.round(value)}%`)
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const datasetIndex = context.datasetIndex;
          const index = context.dataIndex;
          const data = processedData[index];
          
          // Handle drop-a-pin locations or schools without demographic data
          if (enrollmentTrends?.isDropAPinLocation || !selectedSchoolHasDemographicData) {
            if (viewType === 'current') {
              return `${data.current.toFixed(0)}% (${data.currentCount.toLocaleString()} students)`;
            } else {
              const oldCount = data.comparisonCount;
              return `${Math.abs(data.trend).toFixed(0)}% ${data.trend >= 0 ? 'increase' : 'decrease'} ` +
                     `(${oldCount.toLocaleString()} to ${data.currentCount.toLocaleString()} students)`;
            }
          }
          
          // Handle regular schools
          if (viewType === 'current') {
            const value = datasetIndex === 0 ? data.value : data.nearbyValue;
            const count = datasetIndex === 0 ? data.schoolCount : data.nearbyCount;
            return `${value.toFixed(0)}% (${count.toLocaleString()} students)`;
          } else {
            // For trend view
            const change = datasetIndex === 0 ? data.schoolChange : data.nearbyChange;
            const currentCount = datasetIndex === 0 ? data.schoolCount : data.nearbyCount;
            const oldCount = datasetIndex === 0 ? 
              (currentCount / (1 + (data.schoolChange / 100))) :
              (currentCount / (1 + (data.nearbyChange / 100)));
            
            return `${Math.abs(change).toFixed(0)}% ${change >= 0 ? 'increase' : 'decrease'} ` +
                   `(${Math.round(oldCount).toLocaleString()} to ${currentCount.toLocaleString()} students)`;
          }
        }
      }
    }
  },
  scales: {
    x: {
      grid: { color: '#e0e0e0' },
      ticks: {
        callback: (value) => `${Math.round(value)}%`,
        font: {
          weight: 'normal'  // Ensure non-bold font
        }
      },
      border: { display: false },
      suggestedMin: viewType === 'trend' ? -20 : 0,
      suggestedMax: viewType === 'trend' ? 20 : 100
    },
    y: {
      grid: { display: false },
      border: { display: false },
      ticks: {
        font: {
          weight: 'normal'  // Ensure non-bold font
        }
      }
    }
  }
}), [selectedSchool.name, viewType, theme.palette.primary.main, processedData, enrollmentTrends?.isDropAPinLocation, selectedSchoolHasDemographicData]);

const title = (
  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
    <span style={{ flexGrow: 1, fontSize: '1.125rem', fontWeight: 600 }}>
      {enrollmentTrends?.isDropAPinLocation ? 'Area Demographic Enrollment' : 'Demographic Enrollment'}
    </span>
    <ToggleButtonGroup
      value={viewType}
      exclusive
      onChange={handleViewTypeChange}
      size="small"
      sx={{ mr: 1 }}
    >
      <ToggleButton value="current" sx={{ textTransform: 'none' }}>
        2024-25
      </ToggleButton>
      <ToggleButton value="trend" sx={{ textTransform: 'none' }}>
        5-Year Trend
      </ToggleButton>
    </ToggleButtonGroup>
  </Box>
);

  if (!processedData.length) {
    return null;
  }

  return (
    <ChartTemplate
      icon={BarChartOutlined}
      title={title}
      analysis={getAnalysisSummary}
      tooltip={tooltipText}
      chartData={chartData}
      chartOptions={chartOptions}
      additionalInfo={additionalInfo}
      animationEnabled={animationEnabled}
    />
  );
};

export default GeoXEnrollmentCompositionChanges;